// TODO-KPI-03 — Set Yönetimi "Katalogda güncelleme var" bandı: UI kopyası TEK kaynak + saf
// yardımcılar (jsdom yok, kpi-warnings-ignore kalıbı). "Hepsini yoksay" sunucuya gitmez: o anki
// fark İMZASI localStorage'da (kullanıcı + set başına) saklanır; katalog yeniden değişip imza
// değişince band kendiliğinden geri gelir. Sunucu tarafında yok sayma kaydı YOK (kpi_sets kolonu
// eklenmedi).
import type { KpiCatalogChange, KpiCatalogChangeKind, KpiMetric } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import { KPI_PACKAGES_COPY } from "./kpi-packages-copy";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const KPI_CATALOG_SYNC_COPY = {
  band: (n: number): string => `Katalogda ${n.toString()} güncelleme var`,
  bandHint:
    "Katalogdaki etiket/birim/formül değişiklikleri ve kategoriye eklenen yeni göstergeler veri setine kendiliğinden yansımaz.",
  show: "Göster",
  hide: "Gizle",
  applyAll: "Tümünü uygula",
  apply: "Uygula",
  dismissAll: "Hepsini yoksay",
  dismissAllTitle:
    "Bu farkları yoksay (yalnız bu tarayıcıda; katalog yeniden değişirse band geri gelir)",
  dismissed: "Katalog farkları yoksayıldı",
  restore: "yeniden göster",
  blockedHint: "Önce kaydedilmemiş veri seti değişikliklerini kaydedin ya da vazgeçin.",
  applying: "Uygulanıyor…",
  loadFailed: "Katalog farkları yüklenemedi",
  result: (applied: number, failed: number): string =>
    failed > 0
      ? `${applied.toString()} uygulandı · ${failed.toString()} satır uygulanamadı`
      : `${applied.toString()} güncelleme uygulandı`,
  columns: {
    indicator: "Gösterge",
    kind: "Değişiklik",
    catalog: "Katalogdaki",
    set: "Veri setindeki",
  },
  kinds: {
    label: "Etiket",
    unit: "Birim",
    formula: "Varsayılan formül",
    aggregation: "Toplama",
    new_metric: "Yeni gösterge",
    package_new_metric: KPI_PACKAGES_COPY.band.kindNew,
    package_removed_metric: KPI_PACKAGES_COPY.band.kindRemoved,
  } satisfies Record<KpiCatalogChangeKind, string>,
  kindHint: {
    label: "Veri setindeki etiket override'ı kaldırılır; katalog etiketi kullanılır.",
    unit: "Müşterinin birim kaydı kaldırılır, katalog birimine dönülür; kullanımdaki değerler çevrilir (çevrim onayı gerekirse göstergeyi açın).",
    formula:
      "Veri setinin formül listesi katalogdaki varsayılanla değiştirilir (aktif varyant dahil).",
    aggregation: "Veri setindeki toplama override'ı kaldırılır.",
    new_metric: "Gösterge, katalogdaki kategorisinin altına veri setine eklenir; değer girilmez.",
    package_new_metric: "Paketine eklenen gösterge veri setine eklenir; değer girilmez.",
    package_removed_metric: "Gösterge paketten çıkarıldı; veri setinde olduğu gibi kalır.",
  } satisfies Record<KpiCatalogChangeKind, string>,
  newMetricSet: (parentLabel: string | null): string =>
    parentLabel ? `veri setinde yok · ${parentLabel} altına eklenir` : "veri setinde yok",
  empty: "—",
  /** birim varyantı: birim müşteri genelinde (tüm veri setleri) */
  unitClientInfo: (unit: string, catalog: string): string =>
    `müşteri genelinde özel birim: ${unit} (katalog: ${catalog})`,
  openIndicator: "Göstergeyi aç",
} as const;

export function kpiCatalogSyncStorageKey(userId: string, setId: string): string {
  return `mahir.kpi.catalogSyncDismissed.v1.${userId}.${setId}`;
}

export function loadDismissedSignature(
  storage: StorageLike | null | undefined,
  key: string,
): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function saveDismissedSignature(
  storage: StorageLike | null | undefined,
  key: string,
  signature: string | null,
): void {
  try {
    if (signature === null) storage?.removeItem(key);
    else storage?.setItem(key, signature);
  } catch {
    // private window / kota — sessizce geç
  }
}

/** Band gizli mi: imza saklanan imzayla birebir aynıysa. Fark listesi boşsa zaten band yok. */
export function isCatalogSyncDismissed(
  signature: string,
  dismissedSignature: string | null,
): boolean {
  return dismissedSignature !== null && dismissedSignature === signature;
}

/** Satır görünümü: tür etiketi + iki taraflı değer (yeni göstergede "sette yok · X altına eklenir"). */
export function describeCatalogChange(c: KpiCatalogChange): {
  kind: string;
  catalogValue: string;
  setValue: string;
} {
  return {
    kind: KPI_CATALOG_SYNC_COPY.kinds[c.kind],
    catalogValue: c.catalogValue ?? KPI_CATALOG_SYNC_COPY.empty,
    setValue:
      c.kind === "new_metric"
        ? KPI_CATALOG_SYNC_COPY.newMetricSet(c.parentLabel)
        : (c.setValue ?? KPI_CATALOG_SYNC_COPY.empty),
  };
}

/**
 * Kırılım (spec §6.4 son madde): `new_metric` satırı katalogda grup ya da üye ise önerinin dili
 * "X göstergesine yeni kırılım (grup/üye): Y" (X = toplam). Sunucu DTO'su türü taşımaz — tür ve
 * toplam katalogdan okunur; katalog yoksa ya da düz göstergeyse null (eski satır aynen).
 */
export function breakdownSuggestion(
  c: KpiCatalogChange,
  byId: ReadonlyMap<string, KpiMetric>,
): { kind: string; setValue: string; hint: string } | null {
  if (c.kind !== "new_metric") return null;
  const m = byId.get(c.metricId);
  if (!m) return null;
  const S = KPI_BREAKDOWN_COPY.sync;
  if (m.nodeKind === "breakdown_group") {
    const total = m.parentId ? byId.get(m.parentId) : undefined;
    return {
      kind: S.kindGroup,
      setValue: S.group(total?.label ?? c.parentLabel ?? "—", m.label),
      hint: S.hintGroup,
    };
  }
  if (m.dimensionMember) {
    const group = m.parentId ? byId.get(m.parentId) : undefined;
    const total = group?.parentId ? byId.get(group.parentId) : undefined;
    return {
      kind: S.kindMember,
      setValue: S.member(total?.label ?? c.parentLabel ?? "—", m.label),
      hint: S.hintMember,
    };
  }
  return null;
}

// ─── Sektör paketleri DR7 — bant gruplama (saf) ───────────────────────────────────────────────
// `actionable === false` (paketten çıkarılan) bilgi satırıdır: sayaca, "Tümünü uygula"ya ve imzaya
// girmez (imza sunucuda zaten bilgi satırlarını dışlar). Paket satırı (`package_new_metric`) kendi
// bölümünde; katalog türleri (etiket/birim/formül/toplama/yeni gösterge) mevcut tabloda.

type CatalogKind = Exclude<KpiCatalogChangeKind, "package_new_metric" | "package_removed_metric">;
const CATALOG_KIND_ORDER: readonly CatalogKind[] = [
  "formula",
  "label",
  "unit",
  "aggregation",
  "new_metric",
];

export function isActionableChange(c: KpiCatalogChange): boolean {
  return c.actionable !== false;
}

export function isPackageChange(c: KpiCatalogChange): boolean {
  return c.kind === "package_new_metric" || c.kind === "package_removed_metric";
}

export interface CatalogChangeGroups {
  /** eyleme açık tüm satırlar (paket + katalog) — "Tümünü uygula" yükü */
  actionable: KpiCatalogChange[];
  /** eyleme açık paket ekleme satırları */
  packageRows: KpiCatalogChange[];
  /** eyleme açık katalog satırları (mevcut tablo) */
  catalogRows: KpiCatalogChange[];
  /** bilgi satırları (actionable=false) */
  info: KpiCatalogChange[];
  /** bant paket kipinde mi (en az bir paket satırı — aksi eski bant birebir) */
  packageMode: boolean;
}

export function groupCatalogChanges(changes: readonly KpiCatalogChange[]): CatalogChangeGroups {
  const actionable = changes.filter(isActionableChange);
  const info = changes.filter((c) => !isActionableChange(c));
  return {
    actionable,
    packageRows: actionable.filter(isPackageChange),
    catalogRows: actionable.filter((c) => !isPackageChange(c)),
    info,
    packageMode: changes.some(isPackageChange),
  };
}

function packageNameOf(c: KpiCatalogChange): string {
  return c.packageName ?? KPI_PACKAGES_COPY.band.unnamedPackage;
}

/** Katalog satırları tür başına sayı → "1 formül · 2 etiket" (sabit tür sırası). */
export function catalogKindParts(
  rows: readonly KpiCatalogChange[],
  text: Record<CatalogKind, (n: number) => string> = KPI_PACKAGES_COPY.band.catalogKind,
): string[] {
  const counts = new Map<CatalogKind, number>();
  for (const r of rows) {
    if (isPackageChange(r)) continue;
    const k = r.kind as CatalogKind;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return CATALOG_KIND_ORDER.filter((k) => counts.has(k)).map((k) => text[k](counts.get(k)!));
}

/** Paket adına göre (ilk görülme sırası) ekleme / çıkarma sayıları. */
function packageCounts(
  changes: readonly KpiCatalogChange[],
  onlyActionable: boolean,
): { name: string; added: number; removed: number }[] {
  const out = new Map<string, { name: string; added: number; removed: number }>();
  for (const c of changes) {
    if (!isPackageChange(c) || (onlyActionable && !isActionableChange(c))) continue;
    const name = packageNameOf(c);
    const e = out.get(name) ?? { name, added: 0, removed: 0 };
    if (c.kind === "package_new_metric") e.added += 1;
    else e.removed += 1;
    out.set(name, e);
  }
  return [...out.values()];
}

/** DR7 kapalı özet: "<paket>: 2 eklendi · 1 çıkarıldı · Katalog: 1 formül". */
export function closedBandSummary(changes: readonly KpiCatalogChange[]): string {
  const B = KPI_PACKAGES_COPY.band;
  const parts = packageCounts(changes, false).map((p) => B.packagePart(p.name, p.added, p.removed));
  const cat = catalogKindParts(changes.filter(isActionableChange));
  if (cat.length > 0) parts.push(B.catalogPart(cat.join(" · ")));
  return parts.join(" · ");
}

/** DR7 açık başlık (yalnız eyleme açık): "<paket>: 2 ekleme önerisi · Katalog: 1 formül". */
export function openBandTitle(changes: readonly KpiCatalogChange[]): string {
  const B = KPI_PACKAGES_COPY.band;
  const parts = packageCounts(changes, true)
    .filter((p) => p.added > 0)
    .map((p) => B.packageOpenPart(p.name, p.added));
  const cat = catalogKindParts(changes.filter(isActionableChange));
  if (cat.length > 0) parts.push(B.catalogPart(cat.join(" · ")));
  return parts.join(" · ");
}

/** "Tümünü uygula (2 ekleme · 1 formül)" — yalnız eyleme açık satırlar. */
export function applyAllLabel(groups: CatalogChangeGroups): string {
  const B = KPI_PACKAGES_COPY.band;
  const parts: string[] = [];
  if (groups.packageRows.length > 0) parts.push(B.addsPart(groups.packageRows.length));
  parts.push(...catalogKindParts(groups.catalogRows));
  return B.applyAllParts(parts.join(" · "));
}

/** Uygulanacak yük: yalnız eyleme açık satırlar (bilgi satırı asla gönderilmez). */
export function applyPayload(
  items: readonly KpiCatalogChange[],
): { metricId: string; kind: KpiCatalogChangeKind }[] {
  return items.filter(isActionableChange).map((c) => ({ metricId: c.metricId, kind: c.kind }));
}

/**
 * İki parçalı sonuç: "2 eklendi · 1 formül güncellendi · 0 hata". Uygulama sonucu kalem başına
 * ayrım taşımaz — istenen kalemler eksi `failed` üzerinden sayılır. Ata sayısı (spec "atalarıyla 3")
 * sonuç DTO'sunda yok; yazılmaz.
 */
export function applyResultText(
  requested: readonly KpiCatalogChange[],
  failed: readonly { metricId: string; kind: KpiCatalogChangeKind }[],
  /** sunucunun sete eklediği ata sayısı (verilirse "N eklendi (atalarıyla M)") */
  ancestorsAdded?: number,
): string {
  const B = KPI_PACKAGES_COPY.band;
  const failedKeys = new Set(failed.map((f) => `${f.kind}:${f.metricId}`));
  const ok = requested.filter((c) => !failedKeys.has(`${c.kind}:${c.metricId}`));
  const added = ok.filter((c) => c.kind === "package_new_metric").length;
  const parts: string[] = [];
  if (added > 0) {
    parts.push(
      ancestorsAdded !== undefined && ancestorsAdded > 0
        ? B.resultAddedWithAncestors(added, added + ancestorsAdded)
        : B.resultAdded(added),
    );
  }
  parts.push(...catalogKindParts(ok, B.resultKind));
  parts.push(B.resultErrors(failed.length));
  return parts.join(" · ");
}

/** Paket satırı metni: "X pakete eklendi · atalarıyla (N)" (eksik ata yoksa kısa biçim). */
export function packageRowText(c: KpiCatalogChange): string {
  const B = KPI_PACKAGES_COPY.band;
  const n = c.ancestorsMissing ?? 0;
  return n > 0 ? B.row(c.metricLabel, n) : B.rowNoAncestors(c.metricLabel);
}

/**
 * Uygulama sonucu metni + bant sonuç satırı. Kip uygulanan satırlardan DEĞİL, uygulama anındaki
 * durumdan (`statusChanges`) okunur: paket kipinde tek bir katalog satırının "Uygula"sı da iki
 * parçalı satırı günceller; paketsiz kipte satır yok (null — eski sonuç kalmaz).
 */
export function applyOutcome(
  statusChanges: readonly KpiCatalogChange[],
  items: readonly KpiCatalogChange[],
  res: {
    applied: number;
    ancestorsAdded?: number | undefined;
    failed: readonly { metricId: string; kind: KpiCatalogChangeKind }[];
  },
): { text: string; bandResult: string | null } {
  if (!statusChanges.some(isPackageChange)) {
    return { text: KPI_CATALOG_SYNC_COPY.result(res.applied, res.failed.length), bandResult: null };
  }
  const text = applyResultText(items.filter(isActionableChange), res.failed, res.ancestorsAdded);
  return { text, bandResult: text };
}
